/**
 * Parse and format Thai Buddhist dates for med error incident reports.
 * Formats: ต.ค.67, ม.ค. 68, 21 ธ.ค.68, 4 พ.ย.68
 */
import { THAI_SHORT_MONTHS } from './dateThai';
import type { Timestamp } from 'firebase/firestore';

/** Map Thai short month to 0-based month index */
const MONTH_INDEX: Record<string, number> = THAI_SHORT_MONTHS.reduce(
  (acc, m, i) => {
    acc[m] = i;
    acc[m.replace(/\./g, '')] = i; // ต.ค. and ตค
    return acc;
  },
  {} as Record<string, number>,
);

/**
 * Parse Thai Buddhist date string to Date.
 * Handles: ต.ค.67, ม.ค. 68 (month + year, day=1), 21 ธ.ค.68, 4 พ.ย.68 (day + month + year)
 * Short year 67 → 2567 BE → 2024 CE
 */
export function parseThaiBuddhistEventDate(raw: string | null | undefined): Date | null {
  if (!raw || typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s) return null;

  const shortYearMatch = s.match(/(\d{2})\s*$/);
  if (!shortYearMatch) return null;
  const shortYear = parseInt(shortYearMatch[1], 10);
  if (!Number.isFinite(shortYear)) return null;
  // 67 → 2567 BE, 68 → 2568 BE. CE = BE - 543
  const buddhistYear = 2500 + shortYear; // 67 → 2567
  const ceYear = buddhistYear - 543;

  let monthIndex = -1;
  let day = 1;

  // Try "day month year" first (e.g. 21 ธ.ค.68, 4 พ.ย.68)
  for (let i = 0; i < THAI_SHORT_MONTHS.length; i++) {
    const monthLabel = THAI_SHORT_MONTHS[i];
    const regex = new RegExp(
      `(\\d{1,2})\\s*${monthLabel.replace(/\./g, '\\.')}\\s*${shortYear}`,
    );
    const m = s.match(regex);
    if (m) {
      day = Math.min(31, Math.max(1, parseInt(m[1], 10) || 1));
      monthIndex = i;
      break;
    }
  }

  // Try "month year" (e.g. ต.ค.67, ม.ค. 68)
  if (monthIndex < 0) {
    for (let i = 0; i < THAI_SHORT_MONTHS.length; i++) {
      const monthLabel = THAI_SHORT_MONTHS[i];
      const regex = new RegExp(
        `^\\s*${monthLabel.replace(/\./g, '\\.')}\\s*${shortYear}\\s*$`,
      );
      if (regex.test(s)) {
        monthIndex = i;
        break;
      }
    }
  }

  // Fallback: any month string in the value
  if (monthIndex < 0) {
    for (const [label, idx] of Object.entries(MONTH_INDEX)) {
      if (s.includes(label)) {
        monthIndex = idx;
        const dayMatch = s.match(/^(\d{1,2})/);
        if (dayMatch) day = Math.min(31, Math.max(1, parseInt(dayMatch[1], 10) || 1));
        break;
      }
    }
  }

  if (monthIndex < 0) return null;

  const date = new Date(ceYear, monthIndex, day);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** Firestore Timestamp or server value with toDate(). Use for eventDate, lastSyncAt. */
export type FirestoreDateLike = Timestamp | { toDate: () => Date } | Date | null | undefined;

/** Convert Firestore Timestamp or { toDate } to Date, or return null. Reduces ad-hoc casts. */
export function toDateFromFirestore(
  value: FirestoreDateLike,
): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

/**
 * Format date for display as Thai Buddhist "ต.ค. 67" or "21 ธ.ค. 68"
 */
export function formatMedErrorEventDate(
  date: Date | Timestamp | { toDate: () => Date },
): string {
  const d = date instanceof Date ? date : 'toDate' in date ? date.toDate() : (date as Timestamp).toDate();
  const monthIndex = d.getMonth();
  const day = d.getDate();
  const beYear = d.getFullYear() + 543;
  const shortYear = String(beYear).slice(-2);
  const monthLabel = THAI_SHORT_MONTHS[monthIndex] ?? '';
  if (day > 1) {
    return `${day} ${monthLabel} ${shortYear}`;
  }
  return `${monthLabel} ${shortYear}`;
}
